import axios, { AxiosError, AxiosPromise } from 'axios';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import {
  useMutation as useMutationTanstack,
  UseMutationResult as UseMutationResultTanstack,
  useQuery as useQueryTanstack,
} from '@tanstack/react-query';

import routes from 'routes';
import {
  AgreementPreviewLink,
  AgreementStatsType,
  AgreementType,
  Contract,
  OnboardingMilestone,
  UserFormType,
  UserType,
} from 'api/types';
import {
  AnswerNetVideoTPVStatusType,
  WelcomeChecklistItemType,
  WelcomeChecklistType,
} from 'modules/types';
import parsePhoneNumberFromString from 'libphonenumber-js';

export function getAgreement(agreementId: string): AxiosPromise<AgreementType> {
  return axios.get(routes.agreement(agreementId));
}

export function getAgreementHomeownerContractId(
  homeownerContractId: string,
): AxiosPromise<AgreementType[]> {
  return axios.get(routes.homeownerContractId(homeownerContractId));
}

export function getAgreementWorkflowId(workflowId: string): AxiosPromise<AgreementType[]> {
  return axios.get(routes.workflowId(workflowId));
}

export function downloadContract(agreementId: string): AxiosPromise<Contract> {
  return axios.get(routes.contract(agreementId));
}

export function getWelcomeChecklist(agreementId: string): AxiosPromise<WelcomeChecklistItemType[]> {
  return axios.get(routes.welcomeChecklist(agreementId));
}

export function submitWelcomeChecklist({
  agreement,
  welcomeChecklist,
}: {
  agreement: AgreementType;
  welcomeChecklist: WelcomeChecklistItemType[];
}): AxiosPromise<WelcomeChecklistType> {
  return axios.post(routes.welcomeChecklist(agreement.id), {
    checklist: welcomeChecklist,
  });
}

export function updateAgreement(agreementId: string, body: Partial<AgreementType>) {
  return axios.patch(routes.agreement(agreementId), body);
}
export function acknowledgeAuthCopy(agreementId: string) {
  return axios.patch(routes.authCopyAck(agreementId), {
    acknowledged: true,
  });
}

export function uploadAuthCopyAckCsv(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(routes.uploadAuthCopyAckCsv, formData, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
}

export const useGetAgreement = (id: string): UseQueryResult<AgreementType, unknown> =>
  useQuery(['agreement', id], () => axios.get(routes.agreement(id)).then((res) => res.data), {
    enabled: !!id,
  });

type GenerateTpvUrlResponse = {
  url: string;
};

export const useGenerateTpvUrl = ({
  onSuccess,
  onError,
}: {
  onSuccess?: ({ url }: GenerateTpvUrlResponse) => void;
  onError?: (error: AxiosError) => void;
}): UseMutationResult<
  GenerateTpvUrlResponse,
  unknown,
  { agreement_id: string; user_id?: string }
> =>
  useMutation(
    ({ agreement_id, user_id }) =>
      axios.post(routes.generateTpvUrl(agreement_id), { user_id }).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

type GetTpvPreviewUrlsResponse = {
  preview_urls: AgreementPreviewLink[];
};

export const useGetTpvPreviewUrls = ({
  onSuccess,
  onError,
}: {
  onSuccess?: ({ preview_urls }: GetTpvPreviewUrlsResponse) => void;
  onError?: (error: AxiosError) => void;
}): UseMutationResult<GetTpvPreviewUrlsResponse, unknown, { agreement_id: string }> =>
  useMutation(
    ({ agreement_id }) => axios.get(routes.getTpvPreviewUrls(agreement_id)).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

type UseDownloadContractBody = {
  onSuccess?: (data: Contract) => void;
  onError?: (error: AxiosError | Error) => void;
};

export const useDownloadContract = ({
  onSuccess,
  onError,
}: UseDownloadContractBody): UseMutationResult<Contract, unknown, string> =>
  useMutation((id: string) => downloadContract(id).then((res) => res.data), { onSuccess, onError });

export const useAcknowledgeAuthCopy = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}) =>
  useMutation(
    (agreementId: string) =>
      axios
        .patch(routes.authCopyAck(agreementId), {
          acknowledged: true,
        })
        .then((res) => res.data),
    { onSuccess, onError },
  );

export enum AchVerificationResult {
  PASSED = 'PASSED',
  INVALID_ROUTING_NUMBER = 'INVALID_ROUTING_NUMBER',
  INVALID_ACCOUNT_NUMBER = 'INVALID_ACCOUNT_NUMBER',
}

type ValidateAchDataResponse = {
  verification_result: AchVerificationResult;
};

export const useVerifyAchData = ({
  onSuccess,
  onError,
}: {
  onSuccess?: ({ verification_result }: ValidateAchDataResponse) => void;
  onError?: (error: AxiosError) => void;
}): UseMutationResult<
  ValidateAchDataResponse,
  unknown,
  {
    agreement_id: string;
    fetch_bank_details?: boolean;
    account_number?: string;
    routing_number?: string;
  }
> =>
  useMutation(
    ({ agreement_id, account_number, routing_number, fetch_bank_details }) =>
      axios
        .post(routes.verifyAchData(agreement_id), {
          account_number,
          routing_number,
          fetch_bank_details,
        })
        .then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

type UploadAuthCopyAckCsvResponse = {
  before_true: number | null;
  after_true: number | null;
  before_false: number | null;
  after_false: number | null;
};

export const useUploadAuthCopyAckCsv = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (res: UploadAuthCopyAckCsvResponse) => void;
  onError?: (error: AxiosError) => void;
}): UseMutationResult<UploadAuthCopyAckCsvResponse, unknown, { file: File }> =>
  useMutation(
    ({ file }) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios
        .post(routes.uploadAuthCopyAckCsv, formData, {
          headers: {
            'content-type': 'multipart/form-data',
          },
        })
        .then((res) => res.data);
    },
    {
      onSuccess,
      onError,
    },
  );

type GetCurrentTpvStatusResponse = {
  tpv_status: AnswerNetVideoTPVStatusType | null;
};

export function getCurrentTpvStatus(
  agreementId: string,
): AxiosPromise<GetCurrentTpvStatusResponse> {
  return axios.get(routes.getCurrentTpvStatus(agreementId));
}

export const useGetCurrentTpvStatus = ({
  agreementId,
  tpvStatus,
  onSuccess,
  onError,
  refetchInterval,
}: {
  agreementId?: string;
  tpvStatus?: AnswerNetVideoTPVStatusType;
  onSuccess?: (data: GetCurrentTpvStatusResponse) => void;
  onError?: (error: AxiosError) => void;
  refetchInterval: number;
}) =>
  useQuery(
    ['tpvStatus', agreementId],
    () => getCurrentTpvStatus(agreementId || '').then((response) => response.data),
    {
      onSuccess,
      onError,
      enabled: Boolean(agreementId) && tpvStatus === AnswerNetVideoTPVStatusType.PENDING,
      refetchOnWindowFocus: false,
      refetchInterval,
    },
  );

export const useReviewedAgreement = (): UseMutationResultTanstack<
  unknown,
  AxiosError,
  { agreement_id: string }
> =>
  useMutationTanstack({
    mutationFn: ({ agreement_id }) =>
      axios.post(routes.reviewAgreement(agreement_id)).then((res) => res.data),
  });

export const useSetOnboardingMilestone = (): UseMutationResultTanstack<
  unknown,
  AxiosError,
  { agreement_id: string; body: { milestone: OnboardingMilestone | null } }
> =>
  useMutationTanstack({
    mutationFn: ({ agreement_id, body }) =>
      axios.post(routes.setOnboardingMilestone(agreement_id), body).then((res) => res.data),
  });

/* 
|
|       Bottom API CALLS are ONLY for admin dashboard       
|
*/

type GetAgreementsRequestBody = {
  [key: string]: unknown; // search filter + query
  page?: number;
  page_size?: number;
  sort_by?: string;
  direction?: 'ascending' | 'descending';
  internal_users?: boolean;
  has_monitoring?: boolean;
  archived?: boolean;
  milestone?: string[];
};

type GetAgreementsResponse = {
  results: AgreementType[];
  count: number;
};

export const useGetAgreements = (params?: GetAgreementsRequestBody) =>
  useQueryTanstack<GetAgreementsResponse, AxiosError>({
    queryKey: ['agreements', params],
    queryFn: () =>
      axios
        .get(routes.agreements, {
          params,
          paramsSerializer: {
            indexes: null,
          },
        })
        .then((res) => res.data),
  });

export const useGetAgreementStatistics = (): UseQueryResult<AgreementStatsType, unknown> =>
  useQuery(['agreementStatistics'], () =>
    axios.get(routes.agreementStatistics).then((res) => res.data),
  );

type ErrorResponse = {
  detail?: string | string[];
  default_code?: string;
  error_codes?: string;
};

type UseTransferAgreementBody = {
  onSuccess?: (data: TransferAgreementResponse) => void;
  onError?: (error: AxiosError<ErrorResponse>) => void;
};

interface TransferAgreementParams {
  agreement_id: string;
  body: { new_users: UserFormType[] };
}

interface TransferAgreementResponse {
  users: UserType[];
}

export const useTransferAgreement = ({
  onSuccess,
  onError,
}: UseTransferAgreementBody): UseMutationResult<
  TransferAgreementResponse,
  AxiosError,
  TransferAgreementParams
> =>
  useMutation(
    ({ agreement_id, body }) => {
      const { new_users: newUsersBody } = body;

      const newUsers = newUsersBody.map((user) => {
        const { phone, ...userRest } = user;

        return {
          ...userRest,
          phone: {
            phone_number: parsePhoneNumberFromString(phone.phone_number, 'US')?.format('E.164'),
          },
        };
      });
      return axios
        .post(routes.transferAgreement(agreement_id), { new_users: newUsers })
        .then((res) => res.data);
    },
    {
      onSuccess,
      onError,
    },
  );
